import React, { memo, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import './StatCard.scss'

/**
 * StatCard - Displays a single metric with optional trend indicator
 * Memoized for performance optimization.
 *
 * @param {string} title - Metric name
 * @param {string|number} value - Current value
 * @param {string} subtitle - Additional context
 * @param {string} trend - Trend text (e.g., "+12%")
 * @param {boolean} trendUp - Is trend positive
 * @param {string} icon - Icon component or element
 * @param {string} variant - Color variant (primary, success, warning, danger, info)
 * @param {Function} onClick - Click handler for navigation
 * @param {boolean} loading - Show loading skeleton
 */
const StatCard = memo(({
	title,
	value,
	subtitle,
	trend,
	trendUp = true,
	icon,
	variant = 'primary',
	onClick,
	loading = false
}) => {
	// Memoize the card class computation
	const cardClass = useMemo(() =>
		`stat-card stat-card--${variant} ${onClick ? 'stat-card--clickable' : ''}`,
		[variant, onClick]
	)

	// Memoize key press handler
	const handleKeyPress = useCallback((e) => {
		if (e.key === 'Enter' && onClick) {
			onClick()
		}
	}, [onClick])

	if (loading) {
		return (
			<div className={cardClass} aria-busy="true" aria-label={`Loading ${title}`}>
				<div className="stat-card__skeleton">
					<div className="skeleton skeleton--title" aria-hidden="true"></div>
					<div className="skeleton skeleton--value" aria-hidden="true"></div>
					<div className="skeleton skeleton--subtitle" aria-hidden="true"></div>
				</div>
			</div>
		)
	}

	return (
		<div
			className={cardClass}
			onClick={onClick}
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
			onKeyPress={onClick ? handleKeyPress : undefined}
			aria-label={onClick ? `${title}: ${value}. Click to view details` : undefined}
		>
			<div className="stat-card__header">
				<div className="stat-card__info">
					<h3 className="stat-card__title">{title}</h3>
					<p className="stat-card__value">{value}</p>
					{(subtitle || trend) && (
						<p className="stat-card__subtitle">
							{trend && (
								<span className={`stat-card__trend ${trendUp ? 'stat-card__trend--up' : 'stat-card__trend--down'}`}>
									{trendUp ? '↑' : '↓'} {trend}
								</span>
							)}
							{subtitle && <span className="stat-card__subtitle-text">{subtitle}</span>}
						</p>
					)}
				</div>
				{icon && (
					<div className="stat-card__icon">
						{icon}
					</div>
				)}
			</div>
		</div>
	)
})

// Display name for debugging
StatCard.displayName = 'StatCard'

StatCard.propTypes = {
	title: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	subtitle: PropTypes.string,
	trend: PropTypes.string,
	trendUp: PropTypes.bool,
	icon: PropTypes.node,
	variant: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info']),
	onClick: PropTypes.func,
	loading: PropTypes.bool
}

export default StatCard
